/**
 * Item Catalogue Print Engine
 * 3 designs: grid (product cards), list (compact table), gallery (image-focused).
 * Column hide/show is honored per design.
 */

import { formatAmount } from './documentNumbering';
import { addPrintBrand } from './printBranding';

const DEFAULT_CATALOGUE = {
  design: 'grid',
  accentColor: '#800020',
  showCompanyHeader: true,
  columns: {
    image: true,
    itemCode: true,
    itemName: true,
    description: false,
    unitOfMeasure: true,
    barcode: false,
    hsCode: false,
    category: false,
    subCategory: false,
    purchasePrice: false,
    salePrice: true,
    minimumPrice: false,
    wholesalePrice: false,
    dealerPrice: false,
    gstRate: false,
    availableQty: false,
  },
};

export const CATALOGUE_COLUMNS = [
  { key: 'image', label: 'Image' },
  { key: 'itemCode', label: 'Item Code' },
  { key: 'itemName', label: 'Item Name' },
  { key: 'description', label: 'Description' },
  { key: 'unitOfMeasure', label: 'Unit' },
  { key: 'barcode', label: 'Barcode' },
  { key: 'hsCode', label: 'HS Code' },
  { key: 'category', label: 'Category' },
  { key: 'subCategory', label: 'Sub-Category' },
  { key: 'availableQty', label: 'Available Qty' },
  { key: 'purchasePrice', label: 'Purchase Price' },
  { key: 'salePrice', label: 'Sale Price' },
  { key: 'minimumPrice', label: 'Minimum Price' },
  { key: 'wholesalePrice', label: 'Wholesale Price' },
  { key: 'dealerPrice', label: 'Dealer Price' },
  { key: 'gstRate', label: 'Tax Rate' },
];

export const CATALOGUE_DESIGNS = [
  { id: 'grid', name: 'Grid', desc: 'Product cards with image; 3 per row. Best for showcases.' },
  { id: 'list', name: 'List', desc: 'Compact single-table layout. Best for wholesale price lists.' },
  { id: 'gallery', name: 'Gallery', desc: 'Large image alongside full details; 2 per row. Premium look.' },
];

const esc = (s) => String(s ?? '').replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]));

const resolveConfig = (savedConfig) => {
  const global = savedConfig?.global || {};
  const cat = savedConfig?.itemCatalogue || {};
  return {
    ...DEFAULT_CATALOGUE,
    accentColor: global.accentColor || DEFAULT_CATALOGUE.accentColor,
    showCompanyPhone: global.showCompanyPhone !== false,
    showCompanyEmail: global.showCompanyEmail !== false,
    showCompanyNTN: global.showCompanyNTN !== false,
    showCompanyGST: global.showCompanyGST !== false,
    footerText: global.footerText || '',
    ...cat,
    columns: { ...DEFAULT_CATALOGUE.columns, ...(cat.columns || {}) },
  };
};

const getCategoryName = (item, categories, subCategories) => {
  const cat = categories?.find(c => c.id === item.categoryId);
  return cat?.categoryName || '';
};
const getSubCategoryName = (item, subCategories) => {
  const sub = subCategories?.find(c => c.id === item.subCategoryId);
  return sub?.subCategoryName || '';
};

const buildCompanyHeader = (activeCompany, config, title) => {
  const details = [];
  if (activeCompany?.address) details.push(activeCompany.address);
  if (config.showCompanyPhone && activeCompany?.phone) details.push(`Phone: ${activeCompany.phone}`);
  if (config.showCompanyEmail && activeCompany?.email) details.push(`Email: ${activeCompany.email}`);
  if (config.showCompanyNTN && activeCompany?.ntn) details.push(`${(activeCompany?.taxLabel || 'GST').toUpperCase() === 'VAT' ? 'CR' : 'NTN'}: ${activeCompany.ntn}`);
  if (config.showCompanyGST && activeCompany?.gstNumber) details.push(`${activeCompany?.taxLabel || 'GST'}: ${activeCompany.gstNumber}`);
  return `
    <div class="cat-header">
      ${activeCompany?.logoUrl ? `<img class="cat-logo" src="${esc(activeCompany.logoUrl)}" alt="logo" />` : ''}
      <div class="cat-company">
        <div class="cat-company-name">${esc(activeCompany?.name || activeCompany?.companyName || '')}</div>
        ${details.map(d => `<div class="cat-company-detail">${esc(d)}</div>`).join('')}
      </div>
      <div class="cat-title-wrap">
        <div class="cat-title">${esc(title || 'PRODUCT CATALOGUE')}</div>
        <div class="cat-date">Printed: ${new Date().toLocaleDateString()}</div>
      </div>
    </div>`;
};

const fieldValue = (item, key, ctx) => {
  switch (key) {
    case 'image': return item.imageUrl ? `<img class="cat-img" src="${esc(item.imageUrl)}" alt="" />` : '<div class="cat-img-placeholder">No Image</div>';
    case 'itemCode': return esc(item.itemCode || '');
    case 'itemName': return esc(item.itemName || '');
    case 'description': return esc(item.description || '');
    case 'unitOfMeasure': return esc(item.unitOfMeasure || '');
    case 'barcode': return esc(item.barcode || '');
    case 'hsCode': return esc(item.hsCode || '');
    case 'category': return esc(getCategoryName(item, ctx.categories));
    case 'subCategory': return esc(getSubCategoryName(item, ctx.subCategories));
    case 'purchasePrice': return formatAmount(item.purchasePrice || 0);
    case 'salePrice': return formatAmount(item.salePrice || 0);
    case 'minimumPrice': return formatAmount(item.minimumPrice || 0);
    case 'wholesalePrice': return formatAmount(item.wholesalePrice || 0);
    case 'dealerPrice': return formatAmount(item.dealerPrice || 0);
    case 'gstRate': return item.taxable ? `${item.gstRate || 0}%` : '-';
    case 'availableQty': {
      const q = Number(item.availableQty);
      const qty = Number.isFinite(q) ? q : 0;
      return `${qty}${item.unitOfMeasure ? ' ' + esc(item.unitOfMeasure) : ''}`;
    }
    default: return '';
  }
};

// ─── Grid design ────────────────────────────────────────────────────────────
const buildGrid = (items, config, ctx) => {
  const cols = config.columns;
  const card = (item) => {
    const imageHtml = cols.image ? `<div class="g-img">${item.imageUrl ? `<img src="${esc(item.imageUrl)}" alt="" />` : '<span class="g-img-ph">No Image</span>'}</div>` : '';
    const rows = [];
    if (cols.itemName) rows.push(`<div class="g-name">${esc(item.itemName || '')}</div>`);
    if (cols.itemCode) rows.push(`<div class="g-code">Code: ${esc(item.itemCode || '')}</div>`);
    if (cols.description) rows.push(`<div class="g-desc">${esc(item.description || '')}</div>`);
    const metaBits = [];
    if (cols.unitOfMeasure && item.unitOfMeasure) metaBits.push(`UOM: ${esc(item.unitOfMeasure)}`);
    if (cols.barcode && item.barcode) metaBits.push(`Barcode: ${esc(item.barcode)}`);
    if (cols.hsCode && item.hsCode) metaBits.push(`HS: ${esc(item.hsCode)}`);
    if (cols.category) { const c = getCategoryName(item, ctx.categories); if (c) metaBits.push(`Cat: ${esc(c)}`); }
    if (cols.subCategory) { const s = getSubCategoryName(item, ctx.subCategories); if (s) metaBits.push(`Sub: ${esc(s)}`); }
    if (cols.gstRate && item.taxable) metaBits.push(`Tax: ${item.gstRate || 0}%`);
    if (cols.availableQty) {
      const q = Number(item.availableQty);
      const qty = Number.isFinite(q) ? q : 0;
      metaBits.push(`Available: ${qty}${item.unitOfMeasure ? ' ' + esc(item.unitOfMeasure) : ''}`);
    }
    if (metaBits.length) rows.push(`<div class="g-meta">${metaBits.join(' • ')}</div>`);

    const priceBits = [];
    if (cols.salePrice) priceBits.push(`<div class="g-price-main"><span>Sale</span><strong>${formatAmount(item.salePrice || 0)}</strong></div>`);
    if (cols.purchasePrice) priceBits.push(`<div class="g-price"><span>Cost</span><strong>${formatAmount(item.purchasePrice || 0)}</strong></div>`);
    if (cols.wholesalePrice) priceBits.push(`<div class="g-price"><span>Whsl</span><strong>${formatAmount(item.wholesalePrice || 0)}</strong></div>`);
    if (cols.dealerPrice) priceBits.push(`<div class="g-price"><span>Dealer</span><strong>${formatAmount(item.dealerPrice || 0)}</strong></div>`);
    if (cols.minimumPrice) priceBits.push(`<div class="g-price"><span>Min</span><strong>${formatAmount(item.minimumPrice || 0)}</strong></div>`);

    return `<div class="g-card">${imageHtml}<div class="g-body">${rows.join('')}${priceBits.length ? `<div class="g-prices">${priceBits.join('')}</div>` : ''}</div></div>`;
  };
  return `<div class="g-grid">${items.map(card).join('')}</div>`;
};

// ─── List design ────────────────────────────────────────────────────────────
const buildList = (items, config, ctx) => {
  const cols = config.columns;
  const headers = [];
  const fields = [];
  const push = (key, label, align) => { headers.push(`<th${align ? ` style="text-align:${align}"` : ''}>${esc(label)}</th>`); fields.push({ key, align }); };
  if (cols.image) push('image', 'Image');
  if (cols.itemCode) push('itemCode', 'Code');
  if (cols.itemName) push('itemName', 'Item Name');
  if (cols.description) push('description', 'Description');
  if (cols.category) push('category', 'Category');
  if (cols.subCategory) push('subCategory', 'Sub-Category');
  if (cols.unitOfMeasure) push('unitOfMeasure', 'Unit');
  if (cols.barcode) push('barcode', 'Barcode');
  if (cols.hsCode) push('hsCode', 'HS Code');
  if (cols.availableQty) push('availableQty', 'Available Qty', 'right');
  if (cols.gstRate) push('gstRate', 'Tax', 'right');
  if (cols.purchasePrice) push('purchasePrice', 'Purchase', 'right');
  if (cols.salePrice) push('salePrice', 'Sale', 'right');
  if (cols.minimumPrice) push('minimumPrice', 'Min Price', 'right');
  if (cols.wholesalePrice) push('wholesalePrice', 'Wholesale', 'right');
  if (cols.dealerPrice) push('dealerPrice', 'Dealer', 'right');

  if (fields.length === 0) return '<p style="text-align:center;color:#888">No columns selected.</p>';

  const rows = items.map((item, i) => {
    const tds = fields.map(f => {
      const v = fieldValue(item, f.key, ctx);
      const style = f.align ? ` style="text-align:${f.align}"` : '';
      if (f.key === 'image') return `<td${style}>${item.imageUrl ? `<img class="l-img" src="${esc(item.imageUrl)}" alt="" />` : '-'}</td>`;
      return `<td${style}>${v}</td>`;
    }).join('');
    return `<tr>${tds}</tr>`;
  }).join('');

  return `
    <table class="cat-list">
      <thead><tr>${headers.join('')}</tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
};

// ─── Gallery design ─────────────────────────────────────────────────────────
const buildGallery = (items, config, ctx) => {
  const cols = config.columns;
  const tile = (item) => {
    const imageHtml = cols.image ? `<div class="gal-img">${item.imageUrl ? `<img src="${esc(item.imageUrl)}" alt="" />` : '<span class="gal-img-ph">No Image</span>'}</div>` : '';
    const info = [];
    if (cols.itemName) info.push(`<div class="gal-name">${esc(item.itemName || '')}</div>`);
    if (cols.itemCode) info.push(`<div class="gal-code">${esc(item.itemCode || '')}</div>`);
    if (cols.description) info.push(`<div class="gal-desc">${esc(item.description || '')}</div>`);
    const rows = [];
    const addRow = (label, val) => rows.push(`<div class="gal-row"><span>${esc(label)}</span><strong>${val}</strong></div>`);
    if (cols.category) { const c = getCategoryName(item, ctx.categories); if (c) addRow('Category', esc(c)); }
    if (cols.subCategory) { const s = getSubCategoryName(item, ctx.subCategories); if (s) addRow('Sub-Category', esc(s)); }
    if (cols.unitOfMeasure && item.unitOfMeasure) addRow('Unit', esc(item.unitOfMeasure));
    if (cols.barcode && item.barcode) addRow('Barcode', esc(item.barcode));
    if (cols.hsCode && item.hsCode) addRow('HS Code', esc(item.hsCode));
    if (cols.availableQty) {
      const q = Number(item.availableQty);
      const qty = Number.isFinite(q) ? q : 0;
      addRow('Available Qty', `${qty}${item.unitOfMeasure ? ' ' + esc(item.unitOfMeasure) : ''}`);
    }
    if (cols.gstRate) addRow('Tax Rate', item.taxable ? `${item.gstRate || 0}%` : '-');
    if (cols.purchasePrice) addRow('Purchase Price', formatAmount(item.purchasePrice || 0));
    if (cols.salePrice) addRow('Sale Price', formatAmount(item.salePrice || 0));
    if (cols.minimumPrice) addRow('Minimum Price', formatAmount(item.minimumPrice || 0));
    if (cols.wholesalePrice) addRow('Wholesale Price', formatAmount(item.wholesalePrice || 0));
    if (cols.dealerPrice) addRow('Dealer Price', formatAmount(item.dealerPrice || 0));
    return `<div class="gal-tile">${imageHtml}<div class="gal-info">${info.join('')}${rows.length ? `<div class="gal-rows">${rows.join('')}</div>` : ''}</div></div>`;
  };
  return `<div class="gal-grid">${items.map(tile).join('')}</div>`;
};

// ─── CSS ────────────────────────────────────────────────────────────────────
const getCSS = (accent) => `
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; color: #222; margin: 0; padding: 0; }
  .cat-header {
    display: flex; align-items: flex-start; gap: 16px;
    padding: 10px 0 12px 0; border-bottom: 3px solid ${accent}; margin-bottom: 14px;
  }
  .cat-logo { max-width: 70px; max-height: 70px; object-fit: contain; }
  .cat-company { flex: 1; }
  .cat-company-name { font-size: 18px; font-weight: 700; color: ${accent}; }
  .cat-company-detail { font-size: 10px; color: #555; margin-top: 2px; }
  .cat-title-wrap { text-align: right; }
  .cat-title { font-size: 18px; font-weight: 700; color: ${accent}; letter-spacing: 1px; }
  .cat-date { font-size: 10px; color: #777; margin-top: 4px; }
  .cat-footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #ddd; text-align: center; font-size: 10px; color: #777; }
  .summary-bar { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 11px; color: #555; }

  /* Grid design */
  .g-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .g-card { border: 1px solid #e0e0e0; border-radius: 6px; overflow: hidden; page-break-inside: avoid; background: #fff; }
  .g-img { width: 100%; height: 140px; background: #f7f7f7; display: flex; align-items: center; justify-content: center; overflow: hidden; }
  .g-img img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .g-img-ph { color: #aaa; font-size: 11px; }
  .g-body { padding: 8px 10px; }
  .g-name { font-size: 12px; font-weight: 600; color: #222; line-height: 1.25; margin-bottom: 2px; }
  .g-code { font-size: 10px; color: ${accent}; margin-bottom: 4px; }
  .g-desc { font-size: 10px; color: #666; margin-bottom: 4px; line-height: 1.3; max-height: 30px; overflow: hidden; }
  .g-meta { font-size: 9px; color: #777; margin-bottom: 6px; }
  .g-prices { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; padding-top: 6px; border-top: 1px dashed #e0e0e0; }
  .g-price, .g-price-main { font-size: 10px; display: flex; flex-direction: column; }
  .g-price-main strong { color: ${accent}; font-size: 13px; }
  .g-price span, .g-price-main span { color: #999; font-size: 8px; text-transform: uppercase; }

  /* List design */
  .cat-list { width: 100%; border-collapse: collapse; font-size: 10px; }
  .cat-list th { background: ${accent}; color: white; padding: 6px 8px; text-align: left; font-weight: 600; text-transform: uppercase; font-size: 9px; letter-spacing: 0.3px; }
  .cat-list td { padding: 5px 8px; border-bottom: 1px solid #eee; vertical-align: middle; }
  .cat-list tr:nth-child(even) td { background: #fafafa; }
  .l-img { width: 30px; height: 30px; object-fit: contain; border: 1px solid #eee; border-radius: 3px; }

  /* Gallery design */
  .gal-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
  .gal-tile { display: flex; gap: 12px; border: 1px solid #e0e0e0; border-radius: 6px; padding: 10px; page-break-inside: avoid; background: #fff; }
  .gal-img { flex: 0 0 130px; height: 130px; background: #f7f7f7; display: flex; align-items: center; justify-content: center; border-radius: 4px; overflow: hidden; }
  .gal-img img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .gal-img-ph { color: #aaa; font-size: 10px; }
  .gal-info { flex: 1; display: flex; flex-direction: column; }
  .gal-name { font-size: 13px; font-weight: 700; color: #222; line-height: 1.25; }
  .gal-code { font-size: 10px; color: ${accent}; margin: 2px 0 4px 0; }
  .gal-desc { font-size: 10px; color: #666; margin-bottom: 6px; line-height: 1.3; }
  .gal-rows { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 8px; margin-top: 4px; padding-top: 6px; border-top: 1px solid #eee; }
  .gal-row { display: flex; justify-content: space-between; font-size: 10px; gap: 6px; }
  .gal-row span { color: #888; }
  .gal-row strong { color: #222; font-weight: 600; }
`;

export function printItemCatalogue(items, options, activeCompany, savedConfig) {
  if (!items || items.length === 0) { alert('No items to print.'); return; }

  const base = resolveConfig(savedConfig);
  const config = { ...base, ...options, columns: { ...base.columns, ...(options?.columns || {}) } };
  const ctx = { categories: options?.categories || [], subCategories: options?.subCategories || [] };

  const css = getCSS(config.accentColor);
  const header = config.showCompanyHeader !== false ? buildCompanyHeader(activeCompany, config, options?.title || 'PRODUCT CATALOGUE') : '';
  const summary = `<div class="summary-bar"><span>Total Items: <strong>${items.length}</strong></span>${options?.filterLabel ? `<span>${esc(options.filterLabel)}</span>` : ''}</div>`;

  let body = '';
  if (config.design === 'list') body = buildList(items, config, ctx);
  else if (config.design === 'gallery') body = buildGallery(items, config, ctx);
  else body = buildGrid(items, config, ctx);

  const footer = config.footerText ? `<div class="cat-footer">${esc(config.footerText)}</div>` : '';

  const html = `<html><head><title>Item Catalogue</title><style>${css}</style></head><body>${header}${summary}${body}${footer}</body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  addPrintBrand(w);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}
